import React from "react";
import InputField from "../../common/InputField";
import { IoMdClose, IoMdTrash } from "react-icons/io";
import { notify } from "../../common/Toast";
import { aging } from "../../utlis/apiService";
import { AnimatePresence, motion } from "framer-motion";
import Button from "../../common/Button";
import {
  FaPaperPlane,
  FaSave,
  FaTimes,
  FaClock,
  FaGasPump,
} from "react-icons/fa";
import { MdClose } from "react-icons/md";

const AgingForm = ({
  data,
  onSubmit,
  onClose,
  setFormData,
  formData,
  onMarkDone,
  isSaving = false,
}) => {
  const rawStatus = formData?.status ?? data?.status ?? "";
  const isStatusDone =
    typeof rawStatus === "string" && rawStatus.toLowerCase() === "done";
  const today = new Date().toISOString()?.split("T")[0];

  const parseTimeToMinutes = (t) => {
    if (!t) return null;
    const [h = 0, m = 0] = String(t).split(":").map(Number);
    return h * 60 + m;
  };

  const parseDurationToMinutes = (v) => {
    if (v === "" || v == null) return null;
    const s = String(v).trim();
    if (s.includes(":")) {
      const [h = 0, m = 0] = s.split(":").map(Number);
      return h * 60 + m;
    }
    const n = Number(s);
    return Number.isFinite(n) ? Math.round(n * 60) : null;
  };

  const minutesToHHMM = (mins) => {
    const m = ((mins % 1440) + 1440) % 1440;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return String(h).padStart(2, "0") + ":" + String(mm).padStart(2, "0");
  };

  const handleTimeChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      const startMins = parseTimeToMinutes(updated.in_Time);
      const durMins = parseDurationToMinutes(updated.out_Time);

      if (startMins != null && durMins != null) {
        updated.total_Time = minutesToHHMM(startMins + durMins);
      } else {
        updated.total_Time = "";
      }
      return updated;
    });
  };

  const updateDetailField = (index, field, value) => {
    const updated = [...formData.agingDetails];
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, agingDetails: updated }));
  };

  const handleDeleteDetail = async (index, seq_No) => {
    if (window.confirm("Are you sure you want to delete this row?")) {
      const updated = [...formData.agingDetails];
      updated.splice(index, 1);
      setFormData((prev) => ({ ...prev, agingDetails: updated }));

      if (seq_No > 0) {
        try {
          await aging.agingDetailsDelete(seq_No);
          notify("Detail deleted successfully", "success");
        } catch (error) {
          notify("Failed to delete detail", "error");
        }
      }
    }
  };

  const primaryBtnClass = isStatusDone
    ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
    : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg active:scale-95 transition-all";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 30 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        >
          <div className="bg-gradient-to-r from-primary to-blue-700 text-white px-6 py-4 flex justify-between items-center border-b border-slate-700">
            <div>
              <h2 className="text-white text-xl font-bold tracking-tight uppercase">
                Aging Production Form
              </h2>
              <p className="text-slate-400 text-xs mt-1">
                Extrusion Process Management System
              </p>
            </div>
            <button
              onClick={onClose}
              className="hover:bg-white/20 p-2 rounded-full transition-colors"
            >
              <MdClose size={28} />
            </button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 bg-slate-50/30"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <InputField
                label="Aging No"
                value={data.aging_No || ""}
                disabled
                className="bg-slate-50"
              />
              <InputField
                type="date"
                label="Aging Date"
                value={formData.aging_Date || today}
                onChange={(val) =>
                  setFormData((p) => ({ ...p, aging_Date: val }))
                }
              />
              <InputField
                label="Shift"
                value={formData.shift || ""}
                onChange={(val) => setFormData((p) => ({ ...p, shift: val }))}
              />
              <InputField
                label="Temperature (°C)"
                type="number"
                value={formData.temperature || ""}
                onChange={(val) =>
                  setFormData((p) => ({ ...p, temperature: val }))
                }
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white p-5 rounded-xl border border-blue-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-3 text-blue-100">
                  <FaGasPump size={40} />
                </div>
                <h3 className="text-blue-800 font-bold text-sm mb-4 flex items-center gap-2">
                  GAS CONSUMPTION METRICS
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <InputField
                    label="Start Reading"
                    value={formData.start_Gas_Reading}
                    onChange={(val) =>
                      setFormData((p) => ({ ...p, start_Gas_Reading: val }))
                    }
                  />
                  <InputField
                    label="End Reading"
                    value={formData.end_Gas_Reading}
                    onChange={(val) =>
                      setFormData((p) => ({ ...p, end_Gas_Reading: val }))
                    }
                  />
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100 flex justify-between items-center">
                  <span className="text-blue-600 font-semibold text-sm">
                    TOTAL CONSUMPTION
                  </span>
                  <span className="text-blue-900 font-black text-lg">
                    {(Number(formData.end_Gas_Reading) || 0) -
                      (Number(formData.start_Gas_Reading) || 0)}
                  </span>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-emerald-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-3 text-emerald-100">
                  <FaClock size={40} />
                </div>
                <h3 className="text-emerald-800 font-bold text-sm mb-4 flex items-center gap-2">
                  CYCLE TIMING DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <InputField
                    type="time"
                    label="In Time (Start)"
                    value={formData.in_Time || ""}
                    onChange={(val) => handleTimeChange("in_Time", val)}
                  />
                  <InputField
                    type="number"
                    label="Cycle Duration (Hrs)"
                    value={formData.out_Time || ""}
                    onChange={(val) => handleTimeChange("out_Time", val)}
                  />
                </div>
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100 flex justify-between items-center">
                  <span className="text-emerald-600 font-semibold text-sm">
                    EST. FINISH TIME
                  </span>
                  <span className="text-emerald-900 font-black text-lg">
                    {formData.total_Time || "--:--"}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-bold text-slate-700 text-sm tracking-wide uppercase">
                  Batch Production Details
                </h3>
                <span className="bg-slate-200 text-slate-600 text-[10px] px-2 py-1 rounded-full font-bold">
                  {(formData?.agingDetails || []).length} ROWS
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-slate-100 text-slate-600 text-left">
                      <th className="px-4 py-3 border-b font-bold uppercase text-[11px]">
                        Batch ID
                      </th>
                      <th className="px-4 py-3 border-b font-bold uppercase text-[11px]">
                        Die Number
                      </th>
                      <th className="px-4 py-3 border-b font-bold uppercase text-[11px]">
                        SO No
                      </th>
                      <th className="px-4 py-3 border-b font-bold uppercase text-[11px] text-center">
                        Cut Length
                      </th>
                      <th className="px-4 py-3 border-b font-bold uppercase text-[11px] text-center">
                        Approved
                      </th>
                      <th className="px-4 py-3 border-b font-bold uppercase text-[11px] w-28">
                        Pcs
                      </th>
                      <th className="px-4 py-3 border-b font-bold uppercase text-[11px] w-28">
                        Hardness
                      </th>
                      <th className="px-4 py-3 border-b font-bold uppercase text-[11px]">
                        Remark
                      </th>
                      <th className="px-4 py-3 border-b font-bold uppercase text-[11px] text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {formData?.agingDetails?.map((row, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-slate-50 transition-colors group"
                      >
                        <td className="px-4 py-3 font-medium text-slate-700">
                          {row.batch_Id}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-bold text-blue-600 uppercase">
                              {row.dieNumber || "N/A"}
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono italic">
                              ID: {row.die_Id}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {row.po_No}
                        </td>
                        <td className="px-4 py-3 text-center font-mono">
                          {row.cut_Length}
                        </td>
                        <td className="px-4 py-3 text-center text-emerald-600 font-bold">
                          {row.approved_Pcs}
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            className="w-full border border-slate-200 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-center"
                            value={row.quantity ?? ""}
                            onChange={(e) =>
                              updateDetailField(idx, "quantity", e.target.value)
                            }
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            className="w-full border border-slate-200 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-center"
                            value={row.hardness ?? ""}
                            onChange={(e) =>
                              updateDetailField(idx, "hardness", e.target.value)
                            }
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            className="w-full border border-slate-200 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            placeholder="Add remark..."
                            value={row.remark ?? ""}
                            onChange={(e) =>
                              updateDetailField(idx, "remark", e.target.value)
                            }
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleDeleteDetail(idx, row.seq_No)}
                            className="text-slate-300 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-all"
                            title="Remove row"
                          >
                            <IoMdTrash size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(!formData?.agingDetails ||
                      formData.agingDetails.length === 0) && (
                      <tr>
                        <td
                          colSpan={9}
                          className="p-10 text-center text-slate-400 italic bg-slate-50/50"
                        >
                          No production details available for this aging cycle.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </form>

          <div className="bg-slate-100 px-6 py-4 flex flex-col sm:flex-row justify-end items-center gap-4 border-t border-slate-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Button
                text="Cancel"
                className="flex-1 sm:flex-none bg-white hover:bg-slate-50 text-slate-600 border border-slate-300 px-5 py-2 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                icon={FaTimes}
                onClick={onClose}
              />
              <Button
                text="Mark as Done"
                className={`flex-1 sm:flex-none px-5 py-2 rounded-xl font-bold flex items-center justify-center gap-2 ${
                  isStatusDone
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-slate-700 hover:bg-slate-800 text-white shadow-md active:scale-95"
                }`}
                icon={FaPaperPlane}
                disabled={isStatusDone || isSaving}
                loading={isSaving}
                loadingText="Wait..."
                onClick={onMarkDone}
              />
              <Button
                text="Save Record"
                className={`flex-1 sm:flex-none px-8 py-2 rounded-xl font-bold flex items-center justify-center gap-2 ${primaryBtnClass}`}
                icon={FaSave}
                loading={isSaving}
                disabled={isStatusDone || isSaving}
                type="submit"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AgingForm;
